import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { User } from "firebase/auth";
import {
  decideOauthRequest,
  ensureWorkspace,
  errorMessage,
  getOauthRequest,
  listWorkspace,
} from "../api";
import type { Project, Team } from "../types";

interface RequestInfo {
  clientName: string;
  redirectHost: string;
  scope: string | null;
  resource: string | null;
  projectId: string | null;
}

/**
 * The OAuth consent screen. An MCP client that connects without a static token (Claude
 * Code in Anthropic's cloud, claude.ai) lands its user here from /oauth/authorize, and
 * the answer decides whether that client gets a token for this user's server.
 *
 * The page ends in a full navigation to the client's redirect_uri -- deliberately
 * `window.location.replace`, not a router push: the destination is another origin, and
 * this page should not sit in the back stack waiting to re-answer a spent request.
 */
export default function Consent({ user }: { user: User }) {
  const [params] = useSearchParams();
  const requestId = params.get("request") ?? "";

  const [info, setInfo] = useState<RequestInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deciding, setDeciding] = useState<"approve" | "deny" | null>(null);

  /**
   * The token this approval mints is bound to one server, and a server is per
   * (member, project) -- so approving means saying which project the client gets.
   * A client whose MCP URL is one of the user's servers has already said: that server's
   * project, and no picker, since approving against any other is refused. Otherwise it
   * defaults to the user's own default project, and the picker appears only when there
   * is actually a choice to make.
   */
  const [teams, setTeams] = useState<Team[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState<string>("");

  useEffect(() => {
    if (!requestId) {
      setError("This page needs an authorization request to act on, and none was given.");
      return;
    }
    let cancelled = false;
    getOauthRequest({ requestId })
      .then((res) => {
        if (!cancelled) setInfo(res.data);
      })
      .catch((err) => {
        if (!cancelled) setError(errorMessage(err));
      });

    // ensureWorkspace first: a brand-new user whose first contact is this page has no
    // team or project yet, and it also yields the default to preselect.
    ensureWorkspace()
      .then(async (res) => {
        const ws = await listWorkspace(user.uid);
        if (cancelled) return;
        setTeams(ws.teams);
        setProjects(ws.projects);
        setProjectId(res.data.projectId);
      })
      .catch(() => {
        // Non-fatal: approving without a choice still lands on the default project.
      });

    return () => {
      cancelled = true;
    };
  }, [requestId, user.uid]);

  const pinnedProject = info?.projectId ?? null;
  const chosenProject = pinnedProject ?? projectId;
  const pinnedName = projects.find((p) => p.id === pinnedProject)?.name;

  async function decide(approve: boolean) {
    setDeciding(approve ? "approve" : "deny");
    try {
      const res = await decideOauthRequest({
        requestId,
        approve,
        ...(chosenProject ? { projectId: chosenProject } : {}),
      });
      window.location.replace(res.data.redirectTo);
    } catch (err) {
      setError(errorMessage(err));
      setDeciding(null);
    }
  }

  return (
    <div className="login">
      <div className="login-card">
        <div className="login-mark">🔌</div>
        <h1>MochiConnector</h1>

        {error ? (
          <>
            <p className="login-error">{error}</p>
            <p className="consent-note">
              You can close this tab, or go to your <Link to="/setup">dashboard</Link>.
            </p>
          </>
        ) : !info ? (
          <div className="spinner" />
        ) : (
          <>
            <p className="login-tagline">
              <strong>{info.clientName}</strong> ({info.redirectHost}) is asking to use
              your MCP server.
            </p>
            <p className="consent-note">
              It will see and call every tool your server currently exposes — which
              accounts and tools that is stays under your control on the Providers page,
              and rotating your token on Setup signs it out again.
            </p>
            <p className="consent-note">
              Approving as <strong>{user.email}</strong>.
            </p>

            {/* Which of the user's servers the client is authorized against. Only shown
                when there is a real choice -- one project means one possible answer, and
                so does a client that already names its server. */}
            {pinnedName && projects.length > 1 && (
              <p className="consent-note">
                For project <strong>{pinnedName}</strong>.
              </p>
            )}
            {!pinnedProject && projects.length > 1 && (
              <label className="consent-project">
                <span>For project</span>
                <select
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                  disabled={deciding !== null}
                >
                  {teams.map((team) => (
                    // Same presentation as the dashboard's switcher: the personal team
                    // is where private projects live, not a team with a name.
                    <optgroup
                      key={team.id}
                      label={team.personal ? "Private projects" : team.name}
                    >
                      {projects
                        .filter((p) => p.teamId === team.id)
                        .map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                    </optgroup>
                  ))}
                </select>
              </label>
            )}

            <div className="consent-actions">
              <button
                className="btn"
                onClick={() => decide(true)}
                disabled={deciding !== null}
              >
                {deciding === "approve" ? "Approving…" : "Approve"}
              </button>
              <button
                className="btn btn-ghost"
                onClick={() => decide(false)}
                disabled={deciding !== null}
              >
                {deciding === "deny" ? "Denying…" : "Deny"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
